"use client";

import { useState } from "react";

/**
 * Base site URL used for constructing search result destination URLs.
 */
const WP_BASE_URL = process.env.NEXT_PUBLIC_STRAPI_URL || "https://asktraining.com.sg/";

/**
 * Props for the search form component.
 */
interface SearchFormProps {
  /** Optional CSS class name applied to the form container */
  className?: string;
  /** React ref forwarded to the input element for programmatic focus management */
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

/**
 * Course search input bar used in both desktop dropdown and mobile drawer menus.
 *
 * Redirects the user to the canonical WordPress search results page upon submission.
 *
 * @param props - Component properties
 * @param props.className - Custom CSS class names applied to the `<form>` element
 * @param props.inputRef - Ref forwarded to the `<input>` element for focus control
 * @returns Search form JSX element with search icon submit button
 */
export default function SearchForm({ className, inputRef }: SearchFormProps) {
  const [query, setQuery] = useState("");

  /**
   * Prevents default form reload, trims the query, and triggers window navigation.
   *
   * @param e - Form submission event
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    window.location.href = `${WP_BASE_URL}/search/${encodeURIComponent(trimmed)}/`;
  };


  return (
    <form className={className} onSubmit={handleSubmit} role="search">
      <input
        ref={inputRef}
        type="search"
        name="s"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search..."
        enterKeyHint="search"
        className="ask-search-form__input"
      />
      <button type="submit" className="ask-search-form__submit" aria-label="Search">
        <img src="/assets/images/magnifying.svg" className="ask-search-form__icon" alt="" aria-hidden="true" />
      </button>
    </form>
  );
}
